import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, X, Check, Lock, Utensils, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { foodGroups } from '../data/foodPreferences';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FoodPreferencesPage = () => {
  const { user, hasAccess } = useAuth();
  const [prefs, setPrefs] = useState({}); // { "groupId::Food": 'love' | 'avoid' }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setPref = (key, value) =>
    setPrefs((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));

  const counts = useMemo(() => {
    const vals = Object.values(prefs);
    return {
      love: vals.filter((v) => v === 'love').length,
      avoid: vals.filter((v) => v === 'avoid').length,
    };
  }, [prefs]);

  const handleSubmit = async () => {
    setSubmitting(true);
    const love = [];
    const avoid = [];
    foodGroups.forEach((g) =>
      g.items.forEach((item) => {
        const v = prefs[`${g.id}::${item}`];
        if (v === 'love') love.push(item);
        else if (v === 'avoid') avoid.push(item);
      })
    );
    try {
      await fetch(`${BACKEND_URL}/api/quiz-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          quiz_type: 'food_preferences',
          result: `${love.length} loved · ${avoid.length} to avoid`,
          answers: { love, avoid, notes: notes.trim() },
          created_at: new Date().toISOString(),
        }),
      });
    } catch (e) {
      console.error('Failed to save food preferences', e);
    }
    setSubmitting(false);
    setDone(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Gates ──
  if (!user) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto text-center bg-white rounded-2xl border border-stone-200 p-10" data-testid="food-prefs-login-gate">
          <Lock size={32} className="text-stone-300 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-forest mb-2">Please log in</h1>
          <p className="font-body text-stone-500 mb-6">Sign in with the account Jill set up for you to share your food preferences.</p>
          <Link to="/programs" className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body">Log in from Programs</Link>
        </div>
      </div>
    );
  }
  if (!hasAccess('vip')) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto text-center bg-white rounded-2xl border border-stone-200 p-10" data-testid="food-prefs-vip-gate">
          <Utensils size={32} className="text-terracotta mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-forest mb-2">For private clients</h1>
          <p className="font-body text-stone-500 mb-6">This food-preferences profile is part of private coaching with Jill. If you're working with Jill one-on-one, let her know and she'll switch it on for you.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-terracotta text-white px-6 py-3 rounded-full font-body">Reach out to Jill</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-6">
        <div className="max-w-lg mx-auto text-center bg-white rounded-2xl border border-stone-200 p-10" data-testid="food-prefs-done">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <Check size={28} className="text-emerald-600" />
          </div>
          <h1 className="font-heading text-3xl text-forest mb-3">Thank you!</h1>
          <p className="font-body text-stone-600 mb-2">Your food preferences are saved — Jill can now see them and shape a recipe approach that suits you.</p>
          <p className="font-body text-sm text-stone-400 mb-6">{counts.love} loved · {counts.avoid} to avoid</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => { setDone(false); }} className="inline-flex items-center gap-2 text-forest font-body hover:underline">Edit my answers</button>
            <Link to="/programs" className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body">Back to programs</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="min-h-screen bg-cream pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/programs" className="inline-flex items-center gap-2 text-stone-500 hover:text-forest font-body text-sm mb-6" data-testid="food-prefs-back">
          <ArrowLeft size={16} /> Back to programs
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Utensils size={18} className="text-terracotta" />
            <span className="font-body text-sm tracking-widest uppercase text-stone-400">Private Clients · Food Profile</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-3">Your Food Preferences</h1>
          <p className="font-body text-stone-600 leading-relaxed max-w-2xl">
            Tell Jill what you love and what you'd rather avoid, and she'll build a recipe approach around
            the real you. For each food, tap <span className="text-emerald-600 font-medium">Love it</span> or
            <span className="text-stone-500 font-medium"> Avoid</span> — skip anything you're neutral about. There's
            space at the end for foods not listed and any notes.
          </p>
        </div>

        {/* sticky running total */}
        <div className="sticky top-20 z-20 mb-6 flex items-center gap-3 bg-white/90 backdrop-blur border border-stone-200 rounded-full px-5 py-2.5 w-fit shadow-sm" data-testid="food-prefs-tally">
          <span className="font-body text-sm text-emerald-700 flex items-center gap-1"><Heart size={14} /> {counts.love} loved</span>
          <span className="text-stone-300">·</span>
          <span className="font-body text-sm text-stone-500 flex items-center gap-1"><X size={14} /> {counts.avoid} to avoid</span>
        </div>

        <div className="space-y-8">
          {foodGroups.map((group) => (
            <div key={group.id} className="bg-white rounded-2xl border border-stone-200 p-5 md:p-6" data-testid={`food-group-${group.id}`}>
              <h2 className="font-heading text-xl text-forest mb-1">{group.name}</h2>
              {group.note && <p className="font-body text-xs text-stone-400 mb-4 italic">{group.note}</p>}
              {!group.note && <div className="mb-4" />}
              <ul className="space-y-2">
                {group.items.map((item) => {
                  const key = `${group.id}::${item}`;
                  const val = prefs[key];
                  return (
                    <li key={key} className="flex items-center justify-between gap-3 border-b border-stone-50 pb-2 last:border-0 last:pb-0">
                      <span className={`font-body text-sm ${val === 'avoid' ? 'text-stone-400 line-through' : 'text-forest'}`}>{item}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => setPref(key, 'love')}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-body transition-colors ${val === 'love' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                          data-testid={`food-love-${key}`}
                          aria-pressed={val === 'love'}
                        >
                          <Heart size={12} /> Love it
                        </button>
                        <button
                          onClick={() => setPref(key, 'avoid')}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-body transition-colors ${val === 'avoid' ? 'bg-stone-500 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                          data-testid={`food-avoid-${key}`}
                          aria-pressed={val === 'avoid'}
                        >
                          <X size={12} /> Avoid
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Other foods / notes */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5 md:p-6" data-testid="food-group-notes">
            <h2 className="font-heading text-xl text-forest mb-1">Anything else?</h2>
            <p className="font-body text-xs text-stone-400 mb-4 italic">Foods not listed, allergies, dislikes, cultural favourites, how you like to eat — tell Jill anything.</p>
            <textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Love Thai food · can't do shellfish · dairy bothers me · always short on time for breakfast…"
              className="w-full font-body text-sm text-stone-700 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-1 focus:ring-terracotta"
              data-testid="food-prefs-notes"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-60"
            data-testid="food-prefs-submit"
          >
            {submitting ? <><Loader2 size={18} className="animate-spin" /> Saving…</> : <><Sparkles size={18} /> Send to Jill</>}
          </button>
          <p className="font-body text-sm text-stone-400">Saved to your profile as <span className="font-medium text-stone-500">{user.email}</span></p>
        </div>

        <div className="mt-16">
          <BreathingRoomAttribution />
        </div>
      </div>
    </div>
  );
};

export default FoodPreferencesPage;
